import { useCallback, useEffect, useState } from 'react';
import {
    Badge, Box, Center, Flex, HStack, Icon, Spinner, Text, VStack,
} from '@chakra-ui/react';
import { FiChevronRight, FiMessageCircle } from 'react-icons/fi';
import { Navigate, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import useSocketReconnect from '../hooks/useSocketReconnect';
import { useChatEnabledStatus } from '../mobile/hooks/useChatEnabled';
import useChatSocket from '../mobile/hooks/useChatSocket';
import { isChatChild } from '../utils/institutionFeatures';
import { formatTime } from '../utils/dateUtils';
import LogoPulseLoader from '../components/LogoPulseLoader';

// [B3] 家長對話列表（/parent/chat）：依孩子分組、孩子底下每個課程一串。
// 只列 features.chat=true 機構的孩子（既有 institutionFeatures.js 判斷，見 isChatChild）——
// 跨機構家庭（例如安親哥哥 + 幼稚園妹妹，幼稚園 chat 關閉）只會看到有開 chat 那個孩子的對話；
// 沒有被列出的孩子，聯絡簿仍可透過下方「查看聯絡簿舊版」連結存取（ParentCommBookPage 沒被拿掉）。
export default function ParentChatListPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { enabled, loading: gateLoading } = useChatEnabledStatus();

    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);

    // background=false（預設）只會在下面的掛載 effect 用到一次，此時 loading 本來就已經是
    // 初始值 true，不需要再多一次同步 setLoading(true)（會被 eslint react-hooks/set-state-in-effect
    // 擋下來）；background=true（輪詢/refetch）本來就不動 loading，維持背景更新不閃 spinner。
    const fetchConversations = useCallback((background = false) => {
        return authAPI.chat.conversations()
            .then((r) => setConversations(r.data || []))
            .catch(() => { if (!background) setConversations([]); })
            .finally(() => { if (!background) setLoading(false); });
    }, []);

    useEffect(() => {
        if (!enabled) return; // 旗標還沒查完 / 關閉時不打（下面會導頁或顯示 loading）
        fetchConversations();
    }, [enabled, fetchConversations]);

    // 頁面回到前景時 refetch（比照 NotificationContext.jsx 既有 visibilitychange pattern）
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible' && enabled) {
                fetchConversations(true);
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [enabled, fetchConversations]);

    // 推播抵達時（前景 FCM / 原生 notificationReceived）既有程式碼會 dispatch 'data-refresh'
    // （見 AppParent.jsx、ParentDashboard.jsx 既有慣例）；順手掛上去，回到列表時未讀數才會即時對。
    useEffect(() => {
        const handleRefresh = () => { if (enabled) fetchConversations(true); };
        window.addEventListener('data-refresh', handleRefresh);
        return () => window.removeEventListener('data-refresh', handleRefresh);
    }, [enabled, fetchConversations]);

    // [B3 補強] WS 即時：任何一則 chat_message（不限哪個孩子/課程）都背景重抓整份列表——
    // 量體小，直接重抓比逐筆合併「這則屬於哪個孩子分組、要不要更新未讀/排序」簡單可靠，
    // 比照老師端 feat/b1-chat-ui 的 MobileChatList（ChatInbox）既有寫法。30 秒輪詢/
    // visibilitychange 兩道 fallback 保留不拿掉（見上面兩個 effect）。
    // 第二個 handler（訊息被收回）同樣重抓：後端在收回時會把該對話的 last_message_preview
    // 改成「[訊息已收回]」，不重抓的話被收回的內文會繼續留在列表摘要上；未讀數也會變
    //（後端不把已收回的訊息算成未讀）。
    useChatSocket(() => {
        if (enabled) fetchConversations(true);
    }, () => {
        if (enabled) fetchConversations(true);
    });

    // WS 每次重新連上也重抓一次：斷線期間到達的 chat_message 是**永久遺失**的（WS 不補送），
    // 少了這次重抓，未讀數與最後訊息摘要會停在斷線前的狀態。上面的 visibilitychange 只涵蓋
    // 「App 真的進背景又回來」，涵蓋不到「App 一直開著、只是網路斷了幾秒」那種——而那正是
    // 2026-08-18 正式站日誌看到的形狀（同一位使用者 1.5 秒內重連三次，人根本沒離開畫面）。
    useSocketReconnect(() => {
        if (enabled) fetchConversations(true);
    });

    if (gateLoading) {
        return <Center h="50vh"><LogoPulseLoader /></Center>;
    }
    if (!enabled) {
        // 旗標關（prod 現況）：直接開 /parent/chat 一律導回既有聯絡簿頁，零新入口。
        return <Navigate to="/parent/communication-book" replace />;
    }

    const children = user?.children || [];
    const chatChildIds = new Set(children.filter(isChatChild).map((c) => c.id));
    const hasExcludedChild = children.some((c) => !isChatChild(c));

    const visibleConvs = conversations.filter((c) => chatChildIds.has(c.student_id));
    const groups = children
        .filter((c) => chatChildIds.has(c.id))
        .map((child) => ({
            child,
            convs: visibleConvs
                .filter((c) => c.student_id === child.id)
                .sort((a, b) => {
                    if (!a.last_message_at && !b.last_message_at) return 0;
                    if (!a.last_message_at) return 1;
                    if (!b.last_message_at) return -1;
                    return new Date(b.last_message_at) - new Date(a.last_message_at);
                }),
        }))
        .filter((g) => g.convs.length > 0);

    const openConv = (conv) => {
        navigate(`/parent/chat/${conv.id}`, {
            state: { fromList: true, studentId: conv.student_id, studentName: conv.student_name, className: conv.class_name },
        });
    };

    return (
        <VStack spacing={5} align="stretch" p={4} pb="150px">
            {/* [2026-08-12] 「查看聯絡簿舊版」入口已依 user 要求移除：新版對話已是正式介面，
                多一個舊版出口只會讓家長困惑「哪個才是真的」。
                ⚠️ 但**功能本身刻意保留**：/parent/communication-book 路由與後端端點都不動，
                因為商店上還有未更新的家長 App，它們的 bundle 仍然指向舊頁；
                若把路由或後端一起拿掉，那些家長會直接看不到聯絡簿。
                等到強制更新版本（APP_MIN_VERSION）拉高、確認舊版已淘汰後才可考慮移除。 */}

            {loading ? (
                <Center py={16}><Spinner /></Center>
            ) : groups.length === 0 ? (
                <Flex direction="column" align="center" justify="center" py={20} color="onCanvas.300">
                    <Icon as={FiMessageCircle} boxSize={16} mb={4} />
                    <Text fontSize="xl" mb={2}>目前沒有對話</Text>
                    {hasExcludedChild && (
                        <Text fontSize="sm" textAlign="center" px={6}>
                            部分孩子的聯絡簿功能尚未開放對話，請改用「查看聯絡簿舊版」
                        </Text>
                    )}
                </Flex>
            ) : (
                groups.map(({ child, convs }) => {
                    const unreadTotal = convs.reduce((sum, c) => sum + (c.unread || 0), 0);
                    return (
                        <Box key={child.id}>
                            <HStack mb={2} px={1}>
                                <Text fontSize="lg" fontWeight="bold" color="onCanvas.500">{child.full_name}</Text>
                                {unreadTotal > 0 && (
                                    <Badge colorScheme="red" borderRadius="full" px={2}>{unreadTotal}</Badge>
                                )}
                            </HStack>
                            <VStack spacing={3} align="stretch">
                                {convs.map((conv) => {
                                    const hasUnread = conv.unread > 0;
                                    return (
                                        <Flex
                                            key={conv.id}
                                            as="button"
                                            onClick={() => openConv(conv)}
                                            bg="white"
                                            p={4}
                                            borderRadius="2xl"
                                            shadow="sm"
                                            border="1px solid"
                                            borderColor={hasUnread ? 'brand.200' : 'gray.100'}
                                            borderLeft={hasUnread ? '4px solid' : '1px solid'}
                                            borderLeftColor={hasUnread ? 'brand.400' : 'gray.100'}
                                            align="center"
                                            textAlign="left"
                                            _active={{ transform: 'scale(0.99)' }}
                                            transition="all 0.15s"
                                        >
                                            <Box flex="1" minW={0}>
                                                <Flex justify="space-between" align="center" mb={1}>
                                                    <Text fontWeight={hasUnread ? 'bold' : 'medium'} color="gray.800" noOfLines={1}>
                                                        {conv.class_name || '課程'}
                                                    </Text>
                                                    <Text fontSize="xs" color="onCanvas.300" flexShrink={0} ml={2}>
                                                        {conv.last_message_at ? formatTime(conv.last_message_at) : ''}
                                                    </Text>
                                                </Flex>
                                                <Flex justify="space-between" align="center">
                                                    <Text fontSize="sm" color="onCanvas.400" noOfLines={1} flex="1">
                                                        {conv.last_message_preview || '尚無訊息'}
                                                    </Text>
                                                    {hasUnread && (
                                                        <Badge ml={2} colorScheme="red" borderRadius="full" fontSize="10px" px={1.5}>
                                                            {conv.unread}
                                                        </Badge>
                                                    )}
                                                </Flex>
                                            </Box>
                                            <Icon as={FiChevronRight} color="onCanvas.300" ml={2} flexShrink={0} />
                                        </Flex>
                                    );
                                })}
                            </VStack>
                        </Box>
                    );
                })
            )}
        </VStack>
    );
}
